import { initializeApp, deleteApp } from "firebase/app";
import { getAuth, createUserWithEmailAndPassword, signOut } from "firebase/auth";
import { db, firebaseConfig } from "../firebase/firebaseConfig";
import {
    doc,
    setDoc,
    getDoc,
    getDocs,
    collection,
    deleteDoc,
    serverTimestamp,
} from "firebase/firestore";

/**
 * Fetch a user profile from Firestore by UID
 */
export const getUserProfile = async (uid) => {
    try {
        const userDoc = await getDoc(doc(db, "users", uid));
        if (userDoc.exists()) {
            return { uid: userDoc.id, ...userDoc.data() };
        }
        return null;
    } catch (error) {
        console.error("Error fetching user profile:", error);
        throw error;
    }
};

/**
 * Create or update a user profile document in Firestore
 */
export const createUserProfile = async (uid, data) => {
    try {
        await setDoc(doc(db, "users", uid), {
            ...data,
            updatedAt: serverTimestamp(),
        }, { merge: true });
    } catch (error) {
        console.error("Error creating/updating user profile:", error);
        throw error;
    }
};

/**
 * Get all user profile documents from Firestore
 */
export const getAllUsers = async () => {
    try {
        const snapshot = await getDocs(collection(db, "users"));
        const users = snapshot.docs.map((doc) => ({
            uid: doc.id,
            ...doc.data(),
        }));
        
        // Sort client-side by createdAt descending
        return users.sort((a, b) => {
            const timeA = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0;
            const timeB = b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0;
            return timeB - timeA;
        });
    } catch (error) {
        console.error("Error getting all users:", error);
        throw error;
    }
};

/**
 * Delete a user profile from Firestore
 */
export const deleteUser = async (uid) => {
    try {
        await deleteDoc(doc(db, "users", uid));
    } catch (error) {
        console.error("Error deleting user profile:", error);
        throw error;
    }
};

/**
 * Safe Admin user creation. Initializes a secondary Firebase app instance,
 * creates the user in Auth, logs them out from the secondary session, deletes the app,
 * and writes their profile details to Firestore with the designated role.
 */
export const createUserWithRole = async (email, password, name, role) => {
    let secondaryApp;
    try {
        const appName = `SecondaryApp_${Date.now()}`;
        secondaryApp = initializeApp(firebaseConfig, appName);
        const secondaryAuth = getAuth(secondaryApp);

        // Create the user in auth
        const userCredential = await createUserWithEmailAndPassword(secondaryAuth, email, password);
        const uid = userCredential.user.uid;

        // Immediately sign out secondary auth
        await signOut(secondaryAuth);
        // Clean up the secondary app to prevent memory leaks and name conflicts
        await deleteApp(secondaryApp);

        // Save profile to primary database
        await setDoc(doc(db, "users", uid), {
            name,
            email,
            role,
            createdAt: serverTimestamp(),
        });

        return uid;
    } catch (error) {
        if (secondaryApp) {
            try {
                await deleteApp(secondaryApp);
            } catch (cleanupError) {
                console.error("Error cleaning up secondary app:", cleanupError);
            }
        }
        console.error("Error in createUserWithRole:", error);
        throw error;
    }
};
